import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfessionalBelagomLayout, } from '../components/ProfessionalBelagomLayout';
import { type BelagomView } from '../components/ProfessionalBelagomSidebar';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input } from '../components';
import {
  Upload, FileText, Shield, CheckCircle, AlertCircle, Mail,
  ChevronRight, ChevronLeft, Building2
} from 'lucide-react';
import api from '../services/api';
import { byopApi } from '../services/byopApi';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import {
  CANONICAL_PROFESSIONAL_TYPES,
  PROFESSIONAL_CATEGORIES,
  type ProfessionalCategoryId,
} from '../types/professionalRates';

type Step1Data = z.infer<ReturnType<typeof makeStep1Schema>>;
type Step2Data = { selected_professional_types: string[] };
type Step3Data = z.infer<ReturnType<typeof makeStep3Schema>>;

function makeStep1Schema(t: (key: string) => string, isEdit: boolean) {
  const base = {
    kvk_nummer: z.string().length(8, t('validation.kvkLength')),
    btw_nummer: z.string().min(1, t('validation.btwRequired')),
    bedrijfsnaam: z.string().min(2, t('validation.companyRequired')),
    opgericht_datum: z.string().min(1, t('validation.foundedRequired')),
    voorletters: z.string().min(1, t('validation.initialsRequired')),
    achternaam_professioneel: z.string().min(2, t('validation.lastNameRequired')),
    straatnaam: z.string().min(2, t('validation.streetRequired')),
    huisnummer: z.string().min(1, t('validation.houseNumberRequired')),
    postcode: z.string().regex(/^[1-9][0-9]{3}\s?[A-Z]{2}$/i, t('validation.invalidPostalCode')),
    plaatsnaam: z.string().min(2, t('validation.cityRequired')),
    kvk_uittreksel: isEdit
      ? z.any().optional()
      : z.any().refine((files) => files?.length > 0, t('validation.uploadKvk')),
  };
  return z.object(base);
}

interface SbiCode { code: string; omschrijving: string; hoofdactiviteit: boolean }
interface SbiMatchResult { status: 'match' | 'mismatch' | 'not_configured'; matchedCodes: string[]; requiredCodes: string[] }
interface SbiPlatformMatch { accepted: boolean; matchedCodes: string[]; acceptedCodes: string[] }
interface KvkLookupState {
  status: 'idle' | 'loading' | 'done' | 'error';
  company: { handelsnaam: string | null; straatnaam: string | null; huisnummer: string | null; postcode: string | null; plaats: string | null; oprichtingsdatum: string | null; sbiCodes: SbiCode[] } | null;
  companyError: { code: string; message: string } | null;
  btw: { formatValid: boolean; viesChecked: boolean; viesValid: boolean | null } | null;
  sbi: SbiMatchResult | null;
  platformSbi: SbiPlatformMatch | null;
  errorMessage: string | null;
}

function makeStep2Schema(t: (key: string) => string) {
  return z.object({
    selected_professional_types: z.array(z.string()).min(1, t('validation.selectType')),
  });
}

function makeStep3Schema(t: (key: string) => string, isEdit: boolean) {
  return z.object({
    insurance_company: z.string().min(2, t('validation.insuranceRequired')),
    policy_number: z.string().min(3, t('validation.insuranceRequired')),
    coverage_amount: z.number().min(1000000, t('validation.insuranceRequired')),
    insurance_policy: isEdit
      ? z.any().optional() // bestaande polis mag behouden blijven bij bewerken
      : z.any().refine((files) => files?.length > 0, t('validation.uploadInsurance')),
  });
}

export const ScreeningWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('screening');
  const { user } = useAuthStore();
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Data | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [typeRegistrations, setTypeRegistrations] = useState<Record<string, any>>({});
  const [kvkLookup, setKvkLookup] = useState<KvkLookupState>({
    status: 'idle', company: null, companyError: null, btw: null, sbi: null, platformSbi: null, errorMessage: null,
  });

  // BYOP Legal Gate (migratie 164): door een opdrachtgever uitgenodigde
  // professionals bevestigen twee verklaringen vóór KvK/bankkoppeling.
  const { data: byopStatus } = useQuery({ queryKey: ['byop-status'], queryFn: byopApi.status });
  const byopGate = byopStatus?.gekoppeld && !byopStatus.legal_akkoord ? byopStatus : null;
  const [byopVrijwaring, setByopVrijwaring] = useState(false);
  const [byopAansprakelijkheid, setByopAansprakelijkheid] = useState(false);

  // Fetch screening status - for pre-filling when editing pending screening
  const { data: screeningStatusData, isLoading: isLoadingScreening } = useQuery({
    queryKey: ['screening-status'],
    queryFn: async () => {
      const response = await api.get('/screening/status');
      return response.data?.data;
    },
  });

  const existingScreening = screeningStatusData?.screening;
  // Memo: een nieuwe [] per render liet de prefill-effect elke render opnieuw lopen
  // (formulier reset / render-lus zolang registrations ontbreekt).
  const existingRegistrations = useMemo(() => screeningStatusData?.registrations || [], [screeningStatusData?.registrations]);
  // Herstel-flow: ook een afgekeurde screening is bewerkbaar — bij opslaan
  // gaat het dossier terug naar 'pending' (server herstart de SLA-klok).
  const isEditMode = existingScreening?.status === 'pending' || existingScreening?.status === 'rejected';

  // Pre-fill form when editing existing pending screening
  useEffect(() => {
    if (!isEditMode || !existingScreening) return;

    const types = Array.isArray(existingScreening.selected_professional_types)
      ? existingScreening.selected_professional_types
      : typeof existingScreening.selected_professional_types === 'string'
        ? JSON.parse(existingScreening.selected_professional_types)
        : [];

    setSelectedTypes(types);
    setStep2Data({ selected_professional_types: types });

    const regs: Record<string, any> = {};
    for (const r of existingRegistrations) {
      regs[r.professional_type] = {
        is_registered: r.is_registered || false,
        registration_number: r.registration_number || '',
        not_affiliated: r.not_affiliated || false,
        registration_proof_url: r.registration_proof_url,
        geldig_tot: r.geldig_tot ? String(r.geldig_tot).slice(0, 10) : '',
      };
    }
    setTypeRegistrations(regs);

    const opgerichtDatum = existingScreening.opgericht_datum
      ? new Date(existingScreening.opgericht_datum).toISOString().split('T')[0]
      : '';

    setStep1Data({
      kvk_nummer: existingScreening.kvk_nummer || '',
      btw_nummer: existingScreening.btw_nummer || '',
      bedrijfsnaam: existingScreening.bedrijfsnaam || '',
      opgericht_datum: opgerichtDatum,
      voorletters: existingScreening.voorletters || '',
      achternaam_professioneel: existingScreening.achternaam_professioneel || '',
      straatnaam: existingScreening.straatnaam || '',
      huisnummer: existingScreening.huisnummer || '',
      postcode: existingScreening.postcode || '',
      plaatsnaam: existingScreening.plaatsnaam || '',
      kvk_uittreksel: [],
    });

    if (existingScreening.sbi_codes) {
      const codes = typeof existingScreening.sbi_codes === 'string'
        ? JSON.parse(existingScreening.sbi_codes)
        : existingScreening.sbi_codes;
      setKvkLookup((prev) => ({ ...prev, status: 'done', company: { ...prev.company, sbiCodes: codes } as any }));
    }
  }, [isEditMode, existingScreening, existingRegistrations]);

  // Fetch registration requirements
  const { data: requirementsData } = useQuery({
    queryKey: ['screening-requirements'],
    queryFn: async () => {
      const response = await api.get('/screening/requirements');
      return response.data;
    },
  });

  const requirements = requirementsData?.data || {};

  // Submit screening mutation - uses POST for new, PUT for update
  const submitScreeningMutation = useMutation({
    mutationFn: async ({ formData, isUpdate }: { formData: FormData; isUpdate: boolean }) => {
      const url = isUpdate ? '/screening/update' : '/screening/submit';
      const method = isUpdate ? 'put' : 'post';
      const response = await api.request({
        method,
        url,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      navigate('/professional/screening/pending');
    },
    onError: (error: any) => {
      console.error('Screening submission error:', error);
      alert(error.response?.data?.message || t('info.submitError'));
    },
  });

  const handleFinalSubmit = (step3: Step3Data) => {
    if (!step1Data || !step2Data) return;
    if (byopGate && !(byopVrijwaring && byopAansprakelijkheid)) {
      toast.error('Bevestig beide verklaringen bovenaan deze stap om uw registratie te voltooien.');
      return;
    }

    const formData = new FormData();
    if (byopGate) {
      formData.append('vrijwaring_concurrentiebeding', 'true');
      formData.append('aansprakelijkheid_rechtstreeks', 'true');
    }

    formData.append('kvk_nummer', step1Data.kvk_nummer);
    formData.append('btw_nummer', step1Data.btw_nummer);
    formData.append('bedrijfsnaam', step1Data.bedrijfsnaam);
    formData.append('opgericht_datum', step1Data.opgericht_datum);
    formData.append('voorletters', step1Data.voorletters);
    formData.append('achternaam_professioneel', step1Data.achternaam_professioneel);
    formData.append('straatnaam', step1Data.straatnaam);
    formData.append('huisnummer', step1Data.huisnummer);
    formData.append('postcode', step1Data.postcode);
    formData.append('plaatsnaam', step1Data.plaatsnaam);

    // Only append KvK file when new upload (for new screening or replacement)
    if (step1Data.kvk_uittreksel?.length > 0) {
      formData.append('kvk_uittreksel', step1Data.kvk_uittreksel[0]);
    }

    formData.append('selected_professional_types', JSON.stringify(step2Data.selected_professional_types));

    const registrationsArray = step2Data.selected_professional_types.map(type => {
      const reg = typeRegistrations[type] || {};
      return {
        professional_type: type,
        is_registered: reg.is_registered || false,
        registration_number: reg.registration_number || '',
        registration_organization: requirements[type]?.organization || '',
        not_affiliated: reg.not_affiliated || false,
        geldig_tot: reg.geldig_tot || '',
      };
    });
    formData.append('type_registrations', JSON.stringify(registrationsArray));

    // Append registration proofs - for update use index-based keys; for submit use array
    // plus registration_proof_types (welk type hoort bij welk bestand)
    const proofTypes: string[] = [];
    step2Data.selected_professional_types.forEach((type, index) => {
      const reg = typeRegistrations[type];
      if (reg?.registration_proof?.[0]) {
        if (isEditMode) {
          formData.append(`registration_proof_${index}`, reg.registration_proof[0]);
        } else {
          formData.append('registration_proofs', reg.registration_proof[0]);
          proofTypes.push(type);
        }
      }
    });
    if (!isEditMode) formData.append('registration_proof_types', JSON.stringify(proofTypes));

    formData.append('insurance_company', step3.insurance_company);
    formData.append('policy_number', step3.policy_number);
    formData.append('coverage_amount', step3.coverage_amount.toString());
    if (step3.insurance_policy?.length > 0) {
      formData.append('insurance_policy', step3.insurance_policy[0]);
    }

    submitScreeningMutation.mutate({ formData, isUpdate: isEditMode });
  };

  const handleViewChange = (view: BelagomView) => {
    navigate(`/professional/belagom?view=${view}`);
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email: user.email });
      setResendSent(true);
      toast.success('Verificatielink verstuurd. Check je inbox.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Versturen mislukt. Probeer het later opnieuw.');
    } finally {
      setResending(false);
    }
  };

  if (user && !user.is_verified) {
    return (
      <ProfessionalBelagomLayout activeView="screening" onViewChange={handleViewChange}>
        <div className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <div className="p-6 text-center">
                <Mail className="w-14 h-14 text-blue-500 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">Verifieer eerst je e-mailadres</h1>
                <p className="text-gray-600 mb-6">
                  Voordat je kunt starten met stap 2 (KVK-koppeling) moet je e-mailadres geverifieerd zijn.
                  Check je inbox voor de verificatielink die je bij registratie hebt ontvangen.
                </p>
                {resendSent ? (
                  <p className="text-sm text-green-700">Nieuwe verificatielink verstuurd. Controleer je inbox.</p>
                ) : (
                  <Button onClick={handleResendVerification} loading={resending}>
                    Verificatielink opnieuw versturen
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </div>
      </ProfessionalBelagomLayout>
    );
  }

  if (isLoadingScreening) {
    return (
      <ProfessionalBelagomLayout activeView="screening" onViewChange={handleViewChange}>
        <div className="flex items-center justify-center h-full py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('info.loadingData')}</p>
          </div>
        </div>
      </ProfessionalBelagomLayout>
    );
  }

  return (
    <ProfessionalBelagomLayout activeView="screening" onViewChange={handleViewChange}>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? t('wizard.editTitle') : t('wizard.title')}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditMode
              ? t('wizard.editDescription')
              : t('wizard.description')}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                      currentStep >= step
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                  </div>
                  <span className="text-sm mt-2 font-medium">
                    {step === 1 && t('wizard.step1Title')}
                    {step === 2 && t('wizard.step2Short')}
                    {step === 3 && t('wizard.step3Short')}
                  </span>
                </div>
                {step < 3 && (
                  <div
                    className={`flex-1 h-1 mx-4 ${
                      currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card>
          {currentStep === 1 && (
            <Step1
              isEditMode={isEditMode}
              initialData={step1Data}
              selectedTypes={selectedTypes}
              kvkLookup={kvkLookup}
              setKvkLookup={setKvkLookup}
              onNext={(data) => {
                setStep1Data(data);
                setCurrentStep(2);
              }}
            />
          )}

          {currentStep === 2 && (
            <Step2
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
              typeRegistrations={typeRegistrations}
              setTypeRegistrations={setTypeRegistrations}
              requirements={requirements}
              kvkLookup={kvkLookup}
              achternaam={step1Data?.achternaam_professioneel || ''}
              onBack={() => setCurrentStep(1)}
              onNext={(data) => {
                setStep2Data(data);
                setCurrentStep(3);
              }}
            />
          )}

          {currentStep === 3 && byopGate && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm font-medium text-gray-900">
                U bent uitgenodigd door {byopGate.bedrijfsnaam}. Bevestig de volgende verklaringen om uw registratie te voltooien:
              </p>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={byopVrijwaring}
                  onChange={(e) => setByopVrijwaring(e.target.checked)}
                />
                <span>{byopGate.legal_teksten.vrijwaring_concurrentiebeding}</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={byopAansprakelijkheid}
                  onChange={(e) => setByopAansprakelijkheid(e.target.checked)}
                />
                <span>{byopGate.legal_teksten.aansprakelijkheid_rechtstreeks}</span>
              </label>
            </div>
          )}

          {currentStep === 3 && (
            <Step3
              isEditMode={isEditMode}
              existingInsurance={existingScreening ? {
                insurance_company: existingScreening.insurance_company,
                policy_number: existingScreening.policy_number,
                coverage_amount: existingScreening.coverage_amount,
              } : undefined}
              onBack={() => setCurrentStep(2)}
              onSubmit={handleFinalSubmit}
              isSubmitting={submitScreeningMutation.isPending}
            />
          )}
        </Card>
      </div>
      </div>
    </ProfessionalBelagomLayout>
  );
};

// Step 1 Component
interface Step1Props {
  onNext: (data: Step1Data) => void;
  isEditMode?: boolean;
  initialData?: Step1Data | null;
  selectedTypes: string[];
  kvkLookup: KvkLookupState;
  setKvkLookup: React.Dispatch<React.SetStateAction<KvkLookupState>>;
}

const Step1: React.FC<Step1Props> = ({ onNext, isEditMode, initialData, selectedTypes, kvkLookup, setKvkLookup }) => {
  const { t } = useTranslation('screening');
  const step1Schema = makeStep1Schema(t, !!isEditMode);

  const { register, handleSubmit, formState: { errors }, reset, setValue, getValues } = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: initialData || undefined,
  });

  const handleValidateKvk = async () => {
    const { kvk_nummer, btw_nummer } = getValues();
    if (!kvk_nummer || kvk_nummer.length !== 8 || !btw_nummer) {
      setKvkLookup((prev) => ({ ...prev, status: 'error', errorMessage: t('validation.kvkValidationRequired') }));
      return;
    }

    setKvkLookup((prev) => ({ ...prev, status: 'loading', errorMessage: null }));

    try {
      const response = await api.post('/screening/kvk-lookup', {
        kvk_nummer,
        btw_nummer,
        // Bij een verse aanmelding is dit leeg (functie wordt pas in Stap 2 gekozen;
        // SBI-match wordt daar herberekend via /sbi-check). Bij bewerken of teruggaan
        // vanuit Stap 2 zijn de types bekend en toont Stap 1 direct de SBI-match.
        professional_types: selectedTypes,
      });
      const { company, companyError, btw, sbi, platformSbi } = response.data.data;

      if (company) {
        if (company.handelsnaam) setValue('bedrijfsnaam', company.handelsnaam);
        if (company.oprichtingsdatum) setValue('opgericht_datum', company.oprichtingsdatum);
      }

      setKvkLookup({ status: 'done', company, companyError, btw, sbi, platformSbi: platformSbi ?? null, errorMessage: null });
    } catch (err: any) {
      setKvkLookup({
        status: 'error', company: null, companyError: null, btw: null, sbi: null, platformSbi: null,
        errorMessage: err.response?.data?.message || t('info.kvkLookupError', { message: 'onbekende fout' }),
      });
    }
  };

  React.useEffect(() => {
    if (initialData) reset(initialData);
  }, [initialData, reset]);

  // Harde weigeringspoort: geen enkele SBI-activiteit binnen de door Belagom
  // geaccepteerde codes → niet door naar Stap 2.
  const sbiRejected = kvkLookup.status === 'done' && kvkLookup.platformSbi?.accepted === false;

  const onSubmit = (data: Step1Data) => {
    if (sbiRejected) return;
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <Building2 className="w-6 h-6 mr-2" />
          {t('wizard.step1Heading')}
        </h2>
        <p className="text-gray-600">
          {t('wizard.step1Description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t('label.kvkNumber')}
          {...register('kvk_nummer')}
          error={errors.kvk_nummer?.message}
          placeholder="12345678"
          maxLength={8}
        />

        <Input
          label={t('label.btwNumber')}
          {...register('btw_nummer')}
          error={errors.btw_nummer?.message}
          placeholder={t('label.btwNumberPlaceholder')}
        />
      </div>

      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" onClick={handleValidateKvk} loading={kvkLookup.status === 'loading'}>
          {kvkLookup.status === 'done' ? t('label.revalidate') : t('label.validateKvk')}
        </Button>
        {kvkLookup.status === 'done' && !kvkLookup.companyError && (
          <span className="flex items-center text-sm text-green-700"><CheckCircle className="w-4 h-4 mr-1" /> {kvkLookup.company?.handelsnaam}</span>
        )}
      </div>

      {kvkLookup.status === 'error' && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">{kvkLookup.errorMessage}</div>
      )}

      {kvkLookup.status === 'done' && kvkLookup.companyError && (
        <div className="rounded-md bg-yellow-50 border-l-4 border-yellow-400 p-4 text-sm text-yellow-800">
          {t('info.kvkLookupError', { message: kvkLookup.companyError.message })}
          {kvkLookup.companyError.code === 'not_configured' && <p className="mt-1">{t('info.kvkLookupNotConfigured')}</p>}
        </div>
      )}

      {kvkLookup.status === 'done' && kvkLookup.company && (
        <div className="rounded-md bg-blue-50 p-4 text-sm text-blue-900 space-y-1">
          <p><strong>{kvkLookup.company.handelsnaam}</strong></p>
          <p>{kvkLookup.company.straatnaam} {kvkLookup.company.huisnummer}, {kvkLookup.company.postcode} {kvkLookup.company.plaats}</p>
          {kvkLookup.company.oprichtingsdatum && <p>{t('label.foundedDate')}: {kvkLookup.company.oprichtingsdatum}</p>}
          <p className="pt-1">
            {kvkLookup.btw?.viesValid === true && <span className="text-green-700">✓ {t('info.btwValid')}</span>}
            {kvkLookup.btw?.viesValid !== true && kvkLookup.btw?.formatValid && <span className="text-gray-600">{t('info.btwUnverified')}</span>}
          </p>

          {kvkLookup.company.sbiCodes.length > 0 && (() => {
            const sbi = kvkLookup.sbi;
            const platform = kvkLookup.platformSbi;
            const sbiChecked = sbi != null && sbi.status !== 'not_configured';
            return (
              <div className="pt-2 border-t border-blue-100">
                <p className="font-medium">{t('label.sbiActivities')}</p>
                <ul className="mt-1 space-y-1">
                  {kvkLookup.company.sbiCodes.map((c) => {
                    // Per-code status: getoetst aan de gekozen functie(s) zodra die
                    // bekend zijn, anders aan de volledige geaccepteerde Belagom-lijst.
                    const matched = sbiChecked
                      ? sbi.matchedCodes.includes(c.code)
                      : platform != null
                        ? platform.matchedCodes.includes(c.code)
                        : null;
                    return (
                      <li key={c.code} className="flex items-start">
                        {matched != null ? (
                          matched
                            ? <CheckCircle className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0 text-green-600" />
                            : <AlertCircle className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0 text-amber-500" />
                        ) : (
                          <span className="w-4 mr-1.5 flex-shrink-0" />
                        )}
                        <span>
                          <span className="font-mono">{c.code}</span> — {c.omschrijving}
                          {c.hoofdactiviteit && (
                            <span className="ml-1.5 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">{t('label.mainActivity')}</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {sbiChecked && sbi.status === 'match' && (
                  <p className="mt-2 flex items-center text-green-700">
                    <CheckCircle className="w-4 h-4 mr-1.5 flex-shrink-0" /> {t('info.sbiMatch')}
                  </p>
                )}
                {sbiChecked && sbi.status === 'mismatch' && (
                  <p className="mt-2 flex items-start text-amber-700">
                    <AlertCircle className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0" />
                    <span>
                      {t('info.sbiMismatch', {
                        codes: kvkLookup.company.sbiCodes.map((c) => c.code).join(', '),
                        required: sbi.requiredCodes.join(', '),
                      })}
                    </span>
                  </p>
                )}
                {!sbiChecked && platform?.accepted === true && (
                  <p className="mt-2 flex items-center text-green-700">
                    <CheckCircle className="w-4 h-4 mr-1.5 flex-shrink-0" /> {t('info.sbiPlatformAccepted')}
                  </p>
                )}
                {!sbiChecked && platform == null && (
                  <p className="mt-2 text-gray-600">{t('info.sbiCheckedInStep2')}</p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {kvkLookup.status === 'done' && kvkLookup.platformSbi?.accepted === false && (
        <div className="rounded-md bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3 text-sm text-red-800">
              <p>
                {t('info.sbiPlatformRejected', {
                  codes: (kvkLookup.company?.sbiCodes || []).map((c) => c.code).join(', '),
                })}
              </p>
              <p className="mt-1">
                {t('info.sbiPlatformAcceptedCodes', {
                  accepted: kvkLookup.platformSbi.acceptedCodes.join(', '),
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t('label.companyName')}
          {...register('bedrijfsnaam')}
          error={errors.bedrijfsnaam?.message}
        />

        <Input
          label={t('label.foundedDate')}
          type="date"
          {...register('opgericht_datum')}
          error={errors.opgericht_datum?.message}
        />
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mt-6 mb-4">{t('label.nawDetails')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('label.initials')}
            {...register('voorletters')}
            error={errors.voorletters?.message}
            placeholder="A.B."
          />

          <Input
            label={t('label.lastNameProfessional')}
            {...register('achternaam_professioneel')}
            error={errors.achternaam_professioneel?.message}
            placeholder="Jansen"
          />

          <Input
            label={t('label.street')}
            {...register('straatnaam')}
            error={errors.straatnaam?.message}
            placeholder="Hoofdstraat"
          />

          <Input
            label={t('label.houseNumber')}
            {...register('huisnummer')}
            error={errors.huisnummer?.message}
            placeholder="123"
          />

          <Input
            label={t('label.postalCode')}
            {...register('postcode')}
            error={errors.postcode?.message}
            placeholder="1234 AB"
          />

          <Input
            label={t('label.city')}
            {...register('plaatsnaam')}
            error={errors.plaatsnaam?.message}
            placeholder="Amsterdam"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('label.kvkExtractPdf')} {!isEditMode && '*'}
        </label>
        {isEditMode && (
          <p className="text-sm text-gray-600 mb-2">
            {t('info.kvkAlreadyUploaded')}
          </p>
        )}
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                <span>{t('info.uploadFile')}</span>
                <input
                  type="file"
                  className="sr-only"
                  accept=".pdf"
                  {...register('kvk_uittreksel')}
                />
              </label>
              <p className="pl-1">{t('info.orDragDrop')}</p>
            </div>
            <p className="text-xs text-gray-500">{t('info.pdfMax10mb')}</p>
          </div>
        </div>
        {errors.kvk_uittreksel && (
          <p className="mt-1 text-sm text-red-600">{errors.kvk_uittreksel.message as string}</p>
        )}
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={sbiRejected} className="flex items-center">
          {t('wizard.next')} <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
};

// Step 2 Component
interface Step2Props {
  selectedTypes: string[];
  setSelectedTypes: (types: string[]) => void;
  typeRegistrations: Record<string, any>;
  setTypeRegistrations: (regs: Record<string, any>) => void;
  requirements: Record<string, any>;
  kvkLookup: KvkLookupState;
  /** Voor de naamcontrole bij de live BIG-check */
  achternaam: string;
  onBack: () => void;
  onNext: (data: Step2Data) => void;
}

interface BigCheck {
  status: 'bezig' | 'actief' | string;
  detail?: string;
  nummer: string;
}

const Step2: React.FC<Step2Props> = ({
  selectedTypes,
  setSelectedTypes,
  typeRegistrations,
  setTypeRegistrations,
  requirements,
  kvkLookup,
  achternaam,
  onBack,
  onNext,
}) => {
  const { t } = useTranslation('screening');
  const step2Schema = makeStep2Schema(t);

  const { handleSubmit, formState: { errors }, setValue } = useForm<Step2Data>({
    resolver: zodResolver(step2Schema),
    defaultValues: { selected_professional_types: selectedTypes },
  });

  const [sbiResult, setSbiResult] = useState<SbiMatchResult | null>(null);
  const [sbiChecking, setSbiChecking] = useState(false);
  const sbiCodes = kvkLookup.company?.sbiCodes || [];

  React.useEffect(() => {
    if (selectedTypes.length === 0 || sbiCodes.length === 0) {
      setSbiResult(null);
      return;
    }
    let cancelled = false;
    setSbiChecking(true);
    api.post('/screening/sbi-check', { sbi_codes: sbiCodes, professional_types: selectedTypes })
      .then((response) => { if (!cancelled) setSbiResult(response.data.data.sbi); })
      .catch(() => { if (!cancelled) setSbiResult(null); })
      .finally(() => { if (!cancelled) setSbiChecking(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedTypes), sbiCodes.length]);

  const toggleType = (type: string) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    setSelectedTypes(newTypes);
    setValue('selected_professional_types', newTypes);
  };

  const updateRegistration = (type: string, field: string, value: any) => {
    setTypeRegistrations({
      ...typeRegistrations,
      [type]: {
        ...typeRegistrations[type],
        [field]: value,
      },
    });
  };

  // Live BIG-check (bedrijfsarts, arboverpleegkundige, arbeidsfysiotherapeut):
  // bij 'actief' is geen upload nodig. De server controleert bij indienen opnieuw.
  const [bigChecks, setBigChecks] = useState<Record<string, BigCheck>>({});
  const controleerBig = (type: string) => {
    const nummer = (typeRegistrations[type]?.registration_number || '').trim();
    if (!requirements[type]?.big_check || !nummer || bigChecks[type]?.nummer === nummer) return;
    setBigChecks((b) => ({ ...b, [type]: { status: 'bezig', nummer } }));
    api.post('/screening/big-check', { professional_type: type, registration_number: nummer, achternaam })
      .then((response) => setBigChecks((b) => ({ ...b, [type]: { ...response.data.data, nummer } })))
      .catch(() => setBigChecks((b) => ({ ...b, [type]: { status: 'failed', detail: t('big.failed'), nummer } })));
  };
  const bigActief = (type: string) =>
    bigChecks[type]?.status === 'actief' && bigChecks[type]?.nummer === (typeRegistrations[type]?.registration_number || '').trim();

  // Bewerken: al ingevulde BIG-nummers meteen controleren
  React.useEffect(() => {
    selectedTypes.forEach(controleerBig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedTypes), Object.keys(requirements).length]);

  // Stap 3 upload-gate: voor elk geselecteerd type met een verplichte
  // beroepsvereniging moet zowel een lidmaatschapsnummer als een bewijs
  // aanwezig zijn (nieuw geüpload, of al aanwezig bij het bewerken).
  const missingRequiredRegistrations = selectedTypes.filter((type) => {
    if (!requirements[type]?.required) return false;
    const reg = typeRegistrations[type];
    const hasNumber = !!reg?.registration_number?.trim();
    const hasProof = !!reg?.registration_proof?.[0] || !!reg?.registration_proof_url || bigActief(type);
    return !hasNumber || !hasProof;
  });

  const isBlocked = sbiResult?.status === 'mismatch' || missingRequiredRegistrations.length > 0;

  const onSubmit = (data: Step2Data) => {
    if (isBlocked) return;
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <CheckCircle className="w-6 h-6 mr-2" />
          {t('wizard.step2Heading')}
        </h2>
        <p className="text-gray-600">
          {t('wizard.step2Description')}
        </p>
      </div>

      {errors.selected_professional_types && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{errors.selected_professional_types.message}</p>
            </div>
          </div>
        </div>
      )}

      {sbiCodes.length === 0 && selectedTypes.length > 0 && (
        <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-600">
          {t('info.kvkLookupNotConfigured')}
        </div>
      )}

      {sbiResult?.status === 'mismatch' && (
        <div className="rounded-md bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-800">
              {t('info.sbiMismatch', {
                codes: sbiCodes.map((c: SbiCode) => c.code).join(', '),
                required: sbiResult.requiredCodes.join(', '),
              })}
            </p>
          </div>
        </div>
      )}

      {sbiResult?.status === 'match' && (
        <div className="rounded-md bg-green-50 border-l-4 border-green-400 p-3 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
          <p className="text-sm text-green-800">{t('info.sbiMatch')}</p>
        </div>
      )}

      {sbiResult?.status === 'not_configured' && (
        <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-600">{t('info.sbiNotConfigured')}</div>
      )}

      {missingRequiredRegistrations.length > 0 && (
        <div className="rounded-md bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3 text-sm text-red-800">
              <p>{t('info.registrationRequiredMissing')}</p>
              <ul className="list-disc list-inside mt-1">
                {missingRequiredRegistrations.map((type) => (
                  <li key={type}>
                    {t(`roles.${type}`)} — {requirements[type]?.organization}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
          <div key={catId}>
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">{cat.label}</h4>
            <div className="space-y-3">
        {CANONICAL_PROFESSIONAL_TYPES.filter(type => cat.types.includes(type)).map((type) => {
          const req = requirements[type] || {};
          const isSelected = selectedTypes.includes(type);

          return (
            <div key={type} className="border rounded-lg p-4">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 text-blue-600 rounded"
                  checked={isSelected}
                  onChange={() => toggleType(type)}
                />
                <div className="ml-3 flex-1">
                  <div className="font-medium text-gray-900">{t(`roles.${type}`)}</div>
                  <p className="text-sm text-gray-600 mt-1">{req.description}</p>
                  {req.organization && (
                    <p className="text-xs text-gray-500 mt-1">
                      <strong>{t('label.registration')}:</strong> {req.organization}
                      {req.required ? ` (${t('label.required')})` : ` (${t('label.recommended')})`}
                    </p>
                  )}
                </div>
              </label>

              {isSelected && (
                <div className="mt-4 pl-8 space-y-3 border-l-2 border-blue-200">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`registered-${type}`}
                      className="h-4 w-4 text-blue-600 rounded"
                      checked={typeRegistrations[type]?.is_registered || false}
                      onChange={(e) => updateRegistration(type, 'is_registered', e.target.checked)}
                    />
                    <label htmlFor={`registered-${type}`} className="text-sm text-gray-700">
                      {t('label.registeredAt', { organization: req.organization })}
                    </label>
                  </div>

                  {/* 'Niet aangesloten' optie — niet beschikbaar als registratie voor dit type verplicht is */}
                  {!req.required && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`not-affiliated-${type}`}
                        className="h-4 w-4 text-blue-600 rounded"
                        checked={typeRegistrations[type]?.not_affiliated || false}
                        onChange={(e) => updateRegistration(type, 'not_affiliated', e.target.checked)}
                      />
                      <label htmlFor={`not-affiliated-${type}`} className="text-sm text-gray-700">
                        {t('label.notAffiliated')}
                      </label>
                    </div>
                  )}

                  {(req.required || (typeRegistrations[type]?.is_registered && !typeRegistrations[type]?.not_affiliated)) && (
                    <Input
                      label={req.required ? t('label.registrationNumberRequired') : t('label.registrationNumberOptional')}
                      placeholder={t('label.registrationNumberPlaceholder')}
                      value={typeRegistrations[type]?.registration_number || ''}
                      onChange={(e) => updateRegistration(type, 'registration_number', e.target.value)}
                      onBlur={() => controleerBig(type)}
                    />
                  )}

                  {req.big_check && (
                    <p
                      className={`text-xs ${
                        bigActief(type) ? 'text-green-700' : bigChecks[type] && bigChecks[type].status !== 'bezig' ? 'text-amber-700' : 'text-gray-500'
                      }`}
                    >
                      {bigActief(type) ? (
                        <><CheckCircle className="inline w-3 h-3 mr-1" />{t('big.actief')}</>
                      ) : bigChecks[type]?.status === 'bezig' ? (
                        t('big.bezig')
                      ) : bigChecks[type] ? (
                        <><AlertCircle className="inline w-3 h-3 mr-1" />{bigChecks[type].detail} — {t('big.uploadNodig')}</>
                      ) : (
                        t('big.uitleg')
                      )}
                    </p>
                  )}

                  {!bigActief(type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('label.uploadRegistrationProof')} {req.required && '*'}
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      onChange={(e) => updateRegistration(type, 'registration_proof', e.target.files)}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('info.acceptedFormatsSize')}</p>
                    {(typeRegistrations[type]?.registration_proof?.[0] || typeRegistrations[type]?.registration_proof_url) && (
                      <div className="mt-3">
                        <label htmlFor={`geldig-tot-${type}`} className="block text-sm font-medium text-gray-700 mb-1">
                          {t('manual.validUntil')}
                        </label>
                        <input
                          id={`geldig-tot-${type}`}
                          type="date"
                          min={new Date().toISOString().slice(0, 10)}
                          value={typeRegistrations[type]?.geldig_tot || ''}
                          onChange={(e) => updateRegistration(type, 'geldig_tot', e.target.value)}
                          className="rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('manual.validUntilHelp')}</p>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} className="flex items-center">
          <ChevronLeft className="w-4 h-4 mr-2" /> {t('wizard.previous')}
        </Button>
        <Button type="submit" disabled={isBlocked || sbiChecking} className="flex items-center">
          {t('wizard.next')} <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
};

// Step 3 Component
interface Step3Props {
  onBack: () => void;
  onSubmit: (data: Step3Data) => void;
  isSubmitting: boolean;
  isEditMode?: boolean;
  existingInsurance?: { insurance_company?: string; policy_number?: string; coverage_amount?: number };
}

const Step3: React.FC<Step3Props> = ({ onBack, onSubmit, isSubmitting, isEditMode, existingInsurance }) => {
  const { t } = useTranslation('screening');
  const step3Schema = makeStep3Schema(t, !!isEditMode);

  const { register, handleSubmit, formState: { errors } } = useForm<Step3Data>({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      coverage_amount: existingInsurance?.coverage_amount ? Number(existingInsurance.coverage_amount) : 1000000,
      insurance_company: existingInsurance?.insurance_company || '',
      policy_number: existingInsurance?.policy_number || '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <Shield className="w-6 h-6 mr-2" />
          {t('wizard.step3Heading')}
        </h2>
        <p className="text-gray-600">
          {t('wizard.step3Description')}
        </p>
      </div>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-yellow-400" />
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>{t('label.attention')}:</strong> {t('info.minimumCoverage')}
            </p>
            <p className="text-sm text-yellow-700 mt-1">{t('info.insuranceMandatoryHint')}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t('label.insuranceCompany')}
          {...register('insurance_company')}
          error={errors.insurance_company?.message}
          placeholder={t('label.insuranceCompanyPlaceholder')}
        />

        <Input
          label={t('label.policyNumber')}
          {...register('policy_number')}
          error={errors.policy_number?.message}
        />
      </div>

      <Input
        label={t('label.coverageAmount')}
        type="number"
        {...register('coverage_amount', { valueAsNumber: true })}
        error={errors.coverage_amount?.message}
        placeholder="1000000"
        min="1000000"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('label.insurancePolicyPdf')} {!isEditMode && '*'}
        </label>
        {isEditMode && (
          <p className="text-sm text-gray-600 mb-2">
            {t('info.policyAlreadyUploaded')}
          </p>
        )}
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
          <div className="space-y-1 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                <span>{t('info.uploadFile')}</span>
                <input
                  type="file"
                  className="sr-only"
                  accept=".pdf"
                  {...register('insurance_policy')}
                />
              </label>
              <p className="pl-1">{t('info.orDragDrop')}</p>
            </div>
            <p className="text-xs text-gray-500">{t('info.pdfMax10mbShort')}</p>
          </div>
        </div>
        {errors.insurance_policy && (
          <p className="mt-1 text-sm text-red-600">{errors.insurance_policy.message as string}</p>
        )}
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} className="flex items-center">
          <ChevronLeft className="w-4 h-4 mr-2" /> {t('wizard.previous')}
        </Button>
        <Button type="submit" loading={isSubmitting} className="flex items-center">
          {isEditMode ? t('wizard.saveChanges') : t('wizard.submit')} <CheckCircle className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );
};
